import { Box, Flex, Text } from "@chakra-ui/react"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { useDemoMode } from "@/hooks/useDemoMode"

// Older releases let users dismiss the banner and persisted that to
// sessionStorage. The new contract is non-dismissable, but visitors
// still carrying the old flag would never see the banner again — so we
// proactively clear it on mount.
const LEGACY_DISMISS_KEY = "demo_banner_dismissed"

function pad2(n: number): string {
  return n.toString().padStart(2, "0")
}

// HH:MM:SS countdown to the next demo reset. Always renders three
// zero-padded segments so the banner doesn't shimmy as the clock
// crosses minute / hour boundaries. Returns "00:00:00" once the
// deadline has passed — useDemoMode's 60 s refetch will swing
// ``next_purge_at`` forward to the new window as soon as the purge
// loop runs.
function formatHHMMSS(target: string | null | undefined): string {
  if (!target) return "—"
  const ms = new Date(target).getTime() - Date.now()
  if (!Number.isFinite(ms) || ms <= 0) return "00:00:00"
  const totalSeconds = Math.floor(ms / 1000)
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  return `${pad2(hours)}:${pad2(minutes)}:${pad2(seconds)}`
}

export default function DemoBanner() {
  const { enabled, bannerData } = useDemoMode()
  const { t } = useTranslation("demo")

  // Tick once per second so the countdown updates smoothly. The
  // underlying ``next_purge_at`` comes from useDemoMode's 60 s refresh.
  const [, setTick] = useState(0)
  useEffect(() => {
    if (!enabled) return
    const id = setInterval(() => setTick((n) => n + 1), 1000)
    return () => clearInterval(id)
  }, [enabled])

  // One-shot cleanup of the old dismissal flag.
  useEffect(() => {
    try {
      sessionStorage.removeItem(LEGACY_DISMISS_KEY)
    } catch {
      /* ignore */
    }
  }, [])

  const countdown = useMemo(
    () => formatHHMMSS(bannerData?.next_purge_at),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bannerData?.next_purge_at, /* re-eval on each tick */ Math.floor(Date.now() / 1000)],
  )

  if (!enabled) return null

  // Two text slots flank the HH:MM:SS clock. Server-side overrides
  // saved by an operator in /admin-demo win; otherwise we fall back to
  // the i18n default for that slot. Either default can be empty — the
  // markup below hides slots that resolve to empty strings.
  const leftText =
    bannerData?.banner_left_message ??
    t("banner.leftMessage", "TapDPP demo instance will reset in")
  const rightText =
    bannerData?.banner_right_message ?? t("banner.rightMessage", "")

  return (
    <Box
      position="sticky"
      top={0}
      zIndex={100}
      bg="yellow.400"
      color="black"
      px={4}
      py={2}
      fontWeight="500"
      borderBottom="1px solid"
      borderColor="yellow.500"
      role="status"
      aria-live="polite"
    >
      <Flex
        align="center"
        justify="center"
        gap={3}
        flexWrap="wrap"
        fontSize="sm"
      >
        {leftText && (
          <Text textAlign={{ base: "center", md: "right" }}>{leftText}</Text>
        )}
        <Text
          fontFamily="mono"
          fontWeight="700"
          // Fixed width-ish so the digits don't shimmy as they tick.
          letterSpacing="0.05em"
          fontSize={{ base: "sm", md: "md" }}
          aria-label={t("banner.countdownAriaLabel", "Time until demo reset")}
        >
          {countdown}
        </Text>
        {rightText && (
          <Text textAlign={{ base: "center", md: "left" }}>{rightText}</Text>
        )}
      </Flex>
    </Box>
  )
}
